import React, { useMemo } from 'react';
import { Spinner, Row, Col, Card, Table, Badge } from 'react-bootstrap';
import { useQuery } from 'react-query';
import { Get } from '@app/utils/httpProvider';
import {
    DANHSACH_DUYET_NGOAINGU_API,
    ACCOUNT_API,
    ACCOUNT_AUTHINFO_API,
} from '@app/utils/apiUrl';

// ─── Helpers ────────────────────────────────────────────────────────────────

const parseTrangThai = (val) => {
    if (val === null || val === undefined) return -1;
    if (!isNaN(val)) return Number(val);
    const map = { NhapLieu: 0, ChoDuyet: 1, DaDuyet: 2, TuChoi: 3 };
    return map[String(val).trim()] ?? -1;
};

const parseXacNhanDat = (val) => {
    if (val === null || val === undefined) return null;
    if (val === true || val === 1 || val === '1' || val === 'true') return true;
    if (val === false || val === 0 || val === '0' || val === 'false') return false;
    return null;
};

// Fetch toàn bộ dữ liệu (lấy pageSize lớn để lấy hết 1 lần)
const BIG_PAGE = 9999;

const fetchAllItems = async () => {
    const params = { pageIndex: 1, pageSize: BIG_PAGE, keyword: '' };

    const [chungChiRes, vanBangRes] = await Promise.all([
        Get(DANHSACH_DUYET_NGOAINGU_API.GET_ALL_CHUNGCHINGOAINGU_BY_PAGING, null, params),
        Get(DANHSACH_DUYET_NGOAINGU_API.GET_ALL_VANBANG_BY_PAGING, null, params),
    ]);

    const extract = (res) => {
        const d = res?.data || res;
        const items = d?.$values || d?.data?.$values || d?.data || (Array.isArray(d) ? d : []);
        return Array.isArray(items) ? items : [];
    };

    const chungChiItems = extract(chungChiRes).map((i) => ({ ...i, _sourceType: 'chungChi' }));
    const vanBangItems = extract(vanBangRes).map((i) => ({ ...i, _sourceType: 'vanBang' }));

    // Gộp tất cả văn bằng/chứng chỉ ngoại ngữ và lọc bỏ Nhập liệu (status = 0)
    const allItems = [...chungChiItems, ...vanBangItems].filter(item => {
        const status = item.trangThai ?? item.TrangThai;
        if (status === null || status === undefined) return false; // Thống kê không tính các bản ghi lỗi/null
        if (!isNaN(status)) return Number(status) !== 0;
        return status !== "NhapLieu";
    });

    // Lấy danh sách accountId duy nhất để fetch profile
    const uniqueAccountIds = [...new Set(allItems.map((i) => i.accountId || i.AccountId).filter(Boolean))];

    // Fetch profile (tên, đơn vị) theo từng accountId
    const profileMap = {};
    await Promise.all(
        uniqueAccountIds.map(async (accountId) => {
            try {
                const [authRes, detailRes] = await Promise.all([
                    Get(`${ACCOUNT_AUTHINFO_API.GET_BY_ID_ACCOUNT}/${accountId}`),
                    Get(`${ACCOUNT_API.GET_ACCOUNT_INFO_BYID_FROM_APP_DUNGCHUNG}/${accountId}`),
                ]);
                const auth = authRes?.data || authRes;
                const detail = detailRes?.data || detailRes;

                const fullName = auth ? [
                    auth.lastName || auth.LastName,
                    auth.middleName || auth.MiddleName,
                    auth.firstName || auth.FirstName,
                ].filter((v) => v && String(v).trim() !== '').join(' ') : '-';

                const donVis = detail?.donVis || detail?.DonVis?.$values || detail?.DonVis || [];
                const tenDonVi = (Array.isArray(donVis) && donVis[0])
                    ? (donVis[0].tenDonVi || donVis[0].TenDonVi || 'Chưa có đơn vị')
                    : 'Chưa có đơn vị';

                profileMap[accountId] = { fullName, tenDonVi };
            } catch {
                profileMap[accountId] = { fullName: '-', tenDonVi: 'Chưa có đơn vị' };
            }
        })
    );

    // Gắn thông tin profile vào từng item
    return allItems.map((item) => {
        const accId = item.accountId || item.AccountId;
        const profile = profileMap[accId] || { fullName: '-', tenDonVi: 'Chưa có đơn vị' };
        return {
            ...item,
            _accountId: accId,
            _fullName: profile.fullName,
            _tenDonVi: profile.tenDonVi,
            _trangThai: parseTrangThai(item.trangThai ?? item.TrangThai),
            _xacNhanDat: parseXacNhanDat(item.xacNhanDatYeuCau ?? item.XacNhanDatYeuCau),
        };
    });
};

// ─── Stat Card ───────────────────────────────────────────────────────────────

const StatCard = ({ icon, label, value, color, bg, subtitle }) => (
    <div
        style={{
            background: bg || 'linear-gradient(135deg,#667eea,#764ba2)',
            borderRadius: 16,
            padding: '24px 28px',
            color: '#fff',
            boxShadow: '0 4px 20px rgba(0,0,0,0.10)',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            height: '100%',
        }}
    >
        <div style={{ fontSize: 32, opacity: 0.85 }}>{icon}</div>
        <div style={{ fontSize: 13, fontWeight: 500, opacity: 0.85, letterSpacing: 0.5 }}>{label}</div>
        <div style={{ fontSize: 42, fontWeight: 700, lineHeight: 1.1 }}>{value}</div>
        {subtitle && <div style={{ fontSize: 12, opacity: 0.75 }}>{subtitle}</div>}
    </div>
);

// ─── Mini Dual Bar ──────────────────────────────────────────────────────────

const DualBar = ({ labelA, countA, colorA, labelB, countB, colorB }) => {
    const total = countA + countB;
    const pctA = total ? Math.round((countA / total) * 100) : 0;
    const pctB = 100 - pctA;
    return (
        <div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                <div style={{
                    flex: pctA || 0.01, height: 12, background: colorA,
                    borderRadius: '6px 0 0 6px', transition: 'flex 0.4s',
                    minWidth: countA ? 4 : 0
                }} />
                <div style={{
                    flex: pctB || 0.01, height: 12, background: colorB,
                    borderRadius: '0 6px 6px 0', transition: 'flex 0.4s',
                    minWidth: countB ? 4 : 0
                }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#495057' }}>
                <span><span style={{ color: colorA, fontWeight: 600 }}>●</span> {labelA}: <strong>{countA}</strong> ({pctA}%)</span>
                <span><span style={{ color: colorB, fontWeight: 600 }}>●</span> {labelB}: <strong>{countB}</strong> ({pctB}%)</span>
            </div>
        </div>
    );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const ThongKe = () => {
    const { data: allItems = [], isLoading, isError } = useQuery(
        ['thongKeNgoaiNgu'],
        fetchAllItems,
        { cacheTime: 0, staleTime: 0 }
    );

    // ── 1. Tổng số hồ sơ (account) đã cập nhật văn bằng/chứng chỉ ngoại ngữ
    const soHoSo = useMemo(() => {
        const ids = new Set(allItems.map((i) => i._accountId).filter(Boolean));
        return ids.size;
    }, [allItems]);

    // ── 2. Số VBCC được duyệt / không được duyệt
    const soDuyet = useMemo(() => allItems.filter((i) => i._trangThai === 2).length, [allItems]);
    const soKhongDuyet = useMemo(() => allItems.filter((i) => i._trangThai === 3).length, [allItems]);
    const soChuaDuyet = useMemo(() => allItems.filter((i) => i._trangThai === 1).length, [allItems]);

    // ── 3. Số VBCC đạt / không đạt
    const soDat = useMemo(() => allItems.filter((i) => i._xacNhanDat === true).length, [allItems]);
    const soKhongDat = useMemo(() => allItems.filter((i) => i._xacNhanDat === false).length, [allItems]);

    // ── 4. Số lượng người trong từng đơn vị đã nộp hồ sơ
    const donViStats = useMemo(() => {
        const map = {};
        // Đếm số account duy nhất trong mỗi đơn vị
        const accountPerDonVi = {};
        allItems.forEach((item) => {
            const dv = item._tenDonVi || 'Chưa có đơn vị';
            if (!accountPerDonVi[dv]) accountPerDonVi[dv] = new Set();
            if (item._accountId) accountPerDonVi[dv].add(item._accountId);
            // Đếm số VBCC trong đơn vị
            if (!map[dv]) map[dv] = { soNguoi: 0, soVBCC: 0, soDuyet: 0, soDat: 0 };
            map[dv].soVBCC += 1;
            if (item._trangThai === 2) map[dv].soDuyet += 1;
            if (item._xacNhanDat === true) map[dv].soDat += 1;
        });
        Object.keys(map).forEach((dv) => {
            map[dv].soNguoi = accountPerDonVi[dv]?.size || 0;
        });
        return Object.entries(map)
            .sort((a, b) => b[1].soNguoi - a[1].soNguoi)
            .map(([tenDonVi, stats]) => ({ tenDonVi, ...stats }));
    }, [allItems]);

    const totalVBCC = allItems.length;

    if (isLoading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted fw-semibold">Đang tải dữ liệu thống kê...</p>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="text-center py-5 text-danger">
                <i className="fas fa-exclamation-triangle fa-2x mb-2" />
                <p>Không thể tải dữ liệu thống kê. Vui lòng thử lại.</p>
            </div>
        );
    }

    return (
        <div className="py-3">
            {/* ── KPI Cards ── */}
            <Row className="g-3 mb-4">
                <Col xs={12} md={6} xl={3}>
                    <StatCard
                        icon="👤"
                        label="Tổng số hồ sơ đã cập nhật"
                        value={soHoSo}
                        subtitle={`${totalVBCC} văn bằng / chứng chỉ`}
                        bg="linear-gradient(135deg,#667eea,#764ba2)"
                    />
                </Col>
                <Col xs={12} md={6} xl={3}>
                    <StatCard
                        icon="✅"
                        label="Được duyệt"
                        value={soDuyet}
                        subtitle={`Chờ duyệt: ${soChuaDuyet}`}
                        bg="linear-gradient(135deg,#11998e,#38ef7d)"
                    />
                </Col>
                <Col xs={12} md={6} xl={3}>
                    <StatCard
                        icon="❌"
                        label="Không được duyệt"
                        value={soKhongDuyet}
                        subtitle={``}
                        bg="linear-gradient(135deg,#e53935,#e35d5b)"
                    />
                </Col>
                <Col xs={12} md={6} xl={3}>
                    <StatCard
                        icon="🏆"
                        label="Đạt yêu cầu"
                        value={soDat}
                        subtitle={`Không đạt: ${soKhongDat}`}
                        bg="linear-gradient(135deg,#f7971e,#ffd200)"
                    />
                </Col>
            </Row>

            {/* ── Duyệt vs Không duyệt ── */}
            <Row className="g-3 mb-4">
                <Col xs={12} md={6}>
                    <Card className="h-100 shadow-sm border-0" style={{ borderRadius: 14 }}>
                        <Card.Body>
                            <h6 className="fw-bold mb-3 text-primary">
                                <i className="fas fa-check-circle me-2" />
                                Trạng thái duyệt
                            </h6>
                            <DualBar
                                labelA="Đã duyệt"
                                countA={soDuyet}
                                colorA="#11998e"
                                labelB="Không duyệt"
                                countB={soKhongDuyet}
                                colorB="#e53935"
                            />
                            <div className="mt-3 text-center text-muted" style={{ fontSize: 12 }}>
                                Chờ duyệt: <strong>{soChuaDuyet}</strong> &nbsp;|&nbsp; Tổng VBCC: <strong>{totalVBCC}</strong>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col xs={12} md={6}>
                    <Card className="h-100 shadow-sm border-0" style={{ borderRadius: 14 }}>
                        <Card.Body>
                            <h6 className="fw-bold mb-3 text-warning">
                                <i className="fas fa-star me-2" />
                                Kết quả đạt yêu cầu
                            </h6>
                            <DualBar
                                labelA="Đạt"
                                countA={soDat}
                                colorA="#f7971e"
                                labelB="Không đạt"
                                countB={soKhongDat}
                                colorB="#e53935"
                            />
                            <div className="mt-3 text-center text-muted" style={{ fontSize: 12 }}>
                                Chưa xác nhận: <strong>{totalVBCC - soDat - soKhongDat}</strong> &nbsp;|&nbsp; Tổng VBCC: <strong>{totalVBCC}</strong>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* ── Bảng theo đơn vị ── */}
            <Card className="shadow-sm border-0" style={{ borderRadius: 14 }}>
                <Card.Body>
                    <h6 className="fw-bold mb-3 text-dark">
                        <i className="fas fa-building me-2 text-primary" />
                        Số lượng người nộp hồ sơ theo đơn vị
                    </h6>
                    {donViStats.length === 0 ? (
                        <div className="text-center text-muted py-4">Không có dữ liệu</div>
                    ) : (
                        <div className="table-responsive">
                            <Table bordered hover className="align-middle mb-0" style={{ fontSize: '0.9rem' }}>
                                <thead className="table-light">
                                    <tr className="text-nowrap fw-bold">
                                        <th className="text-center" style={{ width: 50, fontWeight: 700 }}>STT</th>
                                        <th className="text-start" style={{ fontWeight: 700 }}>Đơn vị</th>
                                        <th className="text-center" style={{ minWidth: 110, fontWeight: 700 }}>Số người nộp</th>
                                        <th className="text-center" style={{ minWidth: 100, fontWeight: 700 }}>Được duyệt</th>
                                        <th className="text-center" style={{ minWidth: 100, fontWeight: 700 }}>Đạt yêu cầu</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {donViStats.map((row, idx) => (
                                        <tr key={row.tenDonVi} style={{ color: '#000' }}>
                                            <td className="text-center" style={{ color: '#000' }}>{idx + 1}</td>
                                            <td style={{ color: '#000' }}>
                                                <span className="fw-semibold">{row.tenDonVi}</span>
                                            </td>
                                            <td className="text-center" style={{ color: '#000' }}>
                                                <Badge bg="primary" pill style={{ fontSize: '0.85rem', padding: '5px 12px', color: '#212529' }}>
                                                    {row.soNguoi}
                                                </Badge>
                                            </td>

                                            <td className="text-center" style={{ color: '#000' }}>
                                                {row.soDuyet > 0 ? (
                                                    <Badge bg="success" pill style={{ fontSize: '0.85rem', padding: '5px 10px', color: '#212529' }}>
                                                        {row.soDuyet}
                                                    </Badge>
                                                ) : (
                                                    <span style={{ color: '#000' }}>0</span>
                                                )}
                                            </td>
                                            <td className="text-center" style={{ color: '#000' }}>
                                                {row.soDat > 0 ? (
                                                    <Badge bg="warning" text="dark" pill style={{ fontSize: '0.85rem', padding: '5px 10px', color: '#212529' }}>
                                                        {row.soDat}
                                                    </Badge>
                                                ) : (
                                                    <span style={{ color: '#000' }}>0</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Card.Body>
            </Card>

            <style>{`
                .thongke-section-title {
                    font-size: 0.8rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    color: #6c757d;
                    margin-bottom: 12px;
                }
            `}</style>
        </div>
    );
};

export default ThongKe;
